import { normalizeLegendLayerKey } from 'utils/layerExportProgress';

const SKIPPED_EXPORT_TITLE_SEGMENTS = new Set(['Favourites']);

interface LegendTreeNodeLike {
  readonly key?: string;
  readonly title?: string;
  readonly children?: readonly LegendTreeNodeLike[];
}

function legendKeysMatch(treeKey: string, targetKey: string): boolean {
  return normalizeLegendLayerKey(treeKey) === normalizeLegendLayerKey(targetKey);
}

function sanitizeExportNameSegment(title: string): string {
  return title
    .trim()
    .replace(/\//g, '_')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_()+-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

function findLegendNodeTitlePath(
  targetKey: string,
  nodes: readonly LegendTreeNodeLike[],
  ancestors: readonly string[] = [],
): string[] | null {
  for (const node of nodes) {
    const title = typeof node.title === 'string' ? node.title.trim() : '';
    const key = String(node.key ?? '');
    const currentPath = title ? [...ancestors, title] : [...ancestors];

    if (legendKeysMatch(key, targetKey)) {
      return currentPath;
    }

    if (node.children?.length) {
      const found = findLegendNodeTitlePath(
        targetKey,
        node.children,
        currentPath,
      );
      if (found) {
        return found;
      }
    }
  }

  return null;
}

export function buildLegendLayerExportName(
  node: { readonly key?: string; readonly title?: string },
  legendTreeData?: readonly LegendTreeNodeLike[],
): string {
  const fallbackTitle =
    typeof node.title === 'string' && node.title.trim()
      ? node.title.trim()
      : 'layer';
  const key = String(node.key ?? '');

  const path =
    legendTreeData && legendTreeData.length > 0
      ? findLegendNodeTitlePath(key, legendTreeData)
      : null;

  const segments = (path ?? [fallbackTitle])
    .filter(
      (segment) =>
        segment.length > 0 && !SKIPPED_EXPORT_TITLE_SEGMENTS.has(segment),
    )
    .map(sanitizeExportNameSegment)
    .filter((segment) => segment.length > 0);

  if (segments.length > 0) {
    return segments.join('_');
  }

  const fallback = sanitizeExportNameSegment(fallbackTitle);
  return fallback.length > 0 ? fallback : 'exported_layer';
}
